import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { Row, Col, Label } from "reactstrap";

import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";
import { PLACEHOLDERS, LABELS, NAMES } from "../Config";
import { FormikErrorText } from "../../../components/FormikErrorText";
import GoalStatus, { GOAL_STATUS } from "../../../constants/GoalStatus";
import { VALIDATION_SCHEMA } from "../FormVaildation";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { useRequestProcessor } from "../../../hooks/useRequestProcessors";
import { getKey } from "../../../api/api.keys";
import { CreateAuthor, UpdateAuthor } from "../../../api/api.service";
import RadioGroupV2 from "../../../components/Input/RadioGroupV2";
import { FileUploader } from "../../../components/FileUploader";
import { MediaType } from "../../../constants";
import { finalizeMediaUtil, initializeMediaUtil, uploadOnS3 } from "../../../utils/mediaUtils";
import { showToast, SUCCESS } from "../../../utils/toastUtils";

const AuthorForm = ({ mode, data }) => {
    const history = useHistory();
    const location = useLocation();
    const [isMediaUpload, setIsMediaUpload] = useState(false);
    let { NAME, DEGREE, STATUS, LICENSE, ABOUT_AUTHOR, AUTHOR_IMAGE } = LABELS;

    const GOAL_STATUS_OPTIONS = Object.keys(GOAL_STATUS).map((key) => ({
        label: GoalStatus.getDisplayTextKey(GOAL_STATUS[key]),
        value: key
    }));

    const id = location?.pathname?.split("/").pop() || 0;
    const { useMutate } = useRequestProcessor();

    const { mutate: createAuthor, isLoading } = useMutate("", getKey(), CreateAuthor);
    let { mutate: updateAuthor, isLoading: isUpdateAuthorLoading } = useMutate(
        "",
        getKey(),
        ({ id, bodyData }) => UpdateAuthor(id, bodyData)
    );

    const initialValues = {
        [NAMES[NAME]]: data?.name || undefined,
        [NAMES[STATUS]]: data?.status || null,
        [NAMES[DEGREE]]: data?.degree || undefined,
        [NAMES[ABOUT_AUTHOR]]: data?.about || undefined,
        [NAMES[LICENSE]]: data?.license || undefined,
        imageId: data?.image?.id || null,
        [NAMES[AUTHOR_IMAGE]]: data?.image ? `${data.image.location}${data.image.thumbPath}` : null
    };

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: VALIDATION_SCHEMA,
        onSubmit: async (values) => {
            setIsMediaUpload(true);
            try {
                const uploadResults = await Promise.all(
                    Object.keys(values).map(async (fieldName) => {
                        const fieldData = values[fieldName];

                        if (fieldData?.file) {
                            console.log("Uploading media for field:", fieldName);

                            const mediaId = await handleMediaUpload(
                                fieldData.file,
                                fieldData?.exactFile
                            );

                            return { fieldName, mediaId };
                        }
                        return null;
                    })
                );

                // Attach uploaded media IDs to values
                uploadResults.forEach((result) => {
                    if (result) {
                        const { fieldName, mediaId } = result;
                        values[fieldName].mediaId = mediaId;
                    }
                });
                const bodyData = {
                    name: values.name,
                    status: values.Status,
                    about: values.aboutAuthor,
                    degree: values.degree,
                    license: values.license,
                    imageId: values?.authorImage?.mediaId?.id || data?.thumbPath?.id
                };

                if (mode === "add") {
                    createAuthor(bodyData, {
                        onSuccess: () => {
                            showToast(SUCCESS, "Author Added Successfully!");
                            history.push(APP_ROUTES.AUTHOR_MANAGEMENT);
                        }
                    });
                } else {
                    updateAuthor(
                        { id, bodyData },
                        {
                            onSuccess: () => {
                                showToast(SUCCESS, "Author Updated Successfully!");
                                history.push(APP_ROUTES.AUTHOR_MANAGEMENT);
                            }
                        }
                    );
                }
                setIsMediaUpload(false);
            } catch (error) {
                setIsMediaUpload(false);

                console.error("Error during form submission:", error);
            }
        }
    });

    const handleCancel = () => {
        formik.resetForm();
        history.replace(APP_ROUTES.AUTHOR_MANAGEMENT);
    };

    const handleMediaUpload = async (file, initFile) => {
        if (!file) return null;
        const credentials = await initializeMediaUtil(file);
        await uploadOnS3(initFile, credentials);
        return await finalizeMediaUtil(credentials?.mediaId);
    };

    return (
        <form onSubmit={formik.handleSubmit} className="p-4">
            <Row>
                <Col xs={12} md={6} className="my-2">
                    <TextInput
                        disabled={isLoading || isUpdateAuthorLoading}
                        placeholder={PLACEHOLDERS[LABELS.NAME]}
                        label={`${NAME}`}
                        name={NAMES[NAME]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[NAME]]}
                        className="hide-default-icon"
                        autoFocus
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[NAME]} />
                </Col>
                <Col xs={12} md={6} className="my-2">
                    <TextInput
                        disabled={isLoading || isUpdateAuthorLoading}
                        placeholder={PLACEHOLDERS[LABELS.DEGREE]}
                        label={`${DEGREE}`}
                        name={NAMES[DEGREE]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[DEGREE]]}
                        className="hide-default-icon"
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[DEGREE]} />
                </Col>
                <Col xs={12} className="my-2">
                    <TextInput
                        disabled={isLoading || isUpdateAuthorLoading}
                        placeholder={PLACEHOLDERS[LABELS.LICENSE]}
                        label={`${LICENSE}`}
                        name={NAMES[LICENSE]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[LICENSE]]}
                        className="hide-default-icon"
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[LICENSE]} />
                </Col>
                <Col xs={12} className="my-2">
                    <TextInput
                        disabled={isLoading || isUpdateAuthorLoading}
                        placeholder={PLACEHOLDERS[LABELS.ABOUT_AUTHOR]}
                        label={`${ABOUT_AUTHOR}`}
                        name={NAMES[ABOUT_AUTHOR]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[ABOUT_AUTHOR]]}
                        className="hide-default-icon"
                        type="textarea"
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[ABOUT_AUTHOR]} />
                </Col>
            </Row>
            <Row>
                <Col xs={12} className="my-2">
                    <Label className="mb-2">{AUTHOR_IMAGE}</Label>
                    <FileUploader
                        fileType={MediaType.IMAGE}
                        acceptedFileTypes={["jpg", "png", "jpeg"]}
                        maxFileSize={1}
                        supportedFormats="Supported format: .jpg, .png Max file size: 25MB"
                        onFileUpload={
                            (file, exactFile) =>
                                formik.setFieldValue(NAMES[AUTHOR_IMAGE], { file, exactFile }) // Store only the file
                        }
                        onFileRemove={() => formik.setFieldValue(NAMES[AUTHOR_IMAGE], null)}
                        value={formik.values[NAMES[AUTHOR_IMAGE]]}
                        previewUrl={
                            typeof formik.values[NAMES[AUTHOR_IMAGE]] === "string"
                                ? formik.values[NAMES[AUTHOR_IMAGE]]
                                : null
                        } // Show preview if it's a string URL
                        previewSize={data?.image?.size}
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[AUTHOR_IMAGE]} />
                </Col>
            </Row>

            <Row className="my-4">
                <label className="input-label">{STATUS}</label>

                <RadioGroupV2
                    field={NAMES[STATUS]}
                    name={NAMES[STATUS]}
                    value={formik.values[NAMES[STATUS]]}
                    setFieldValue={formik.setFieldValue}
                    onBlur={formik.handleBlur}
                    options={GOAL_STATUS_OPTIONS}
                />

                <FormikErrorText formikInstance={formik} fieldName={NAMES[STATUS]} />
            </Row>

            <div className="d-flex justify-content-end w-100 my-2 mt-4">
                <CustomButton
                    type="button"
                    title="Cancel"
                    className="me-3 custom-btn-sm"
                    color="primary"
                    outline
                    onClick={handleCancel}
                />
                <CustomButton
                    loading={isMediaUpload || isLoading || isUpdateAuthorLoading}
                    type="submit"
                    title={mode === "add" ? "Save" : "Update"}
                    color="primary"
                    className="shadow-theme grad-primary"
                />
            </div>
        </form>
    );
};

export default AuthorForm;
